import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { ImageSquareIcon, TableIcon } from '@phosphor-icons/react';

/**
 * The vision analysis the intake pipeline substitutes for an embedded image.
 *
 * Emitted by `assessment/intake/extractors/images/enrichment.py` as a
 * ``### Embedded Image N (vision analysis)`` heading followed by a ```json
 * fence. Shape is `VisionAnalysis` in `assessment/llm_response_models.py`;
 * every field has a default there, so all of them are optional here.
 */
export interface VisionAnalysis {
  image_type?: string;
  title?: string;
  summary?: string;
  extracted_text?: string;
  key_information?: string[];
  contains_data?: boolean;
}

/**
 * True when a parsed JSON payload is a vision analysis rather than some other
 * JSON the document happens to contain.
 *
 * Keys are matched rather than validated: the producer defaults every field, so
 * requiring all of them would miss a sparse payload, while `image_type` plus any
 * one content field is enough to be sure.
 */
export function asVisionAnalysis(value: unknown): VisionAnalysis | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const hasType = typeof v.image_type === 'string';
  const hasContent =
    typeof v.extracted_text === 'string' ||
    typeof v.summary === 'string' ||
    Array.isArray(v.key_information);
  return hasType && hasContent ? (v as VisionAnalysis) : null;
}

/** Splits the vision model's flat `extracted_text` into rows of cells. */
function toRows(text: string): string[][] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.split(/\s{2,}|\t/).map((c) => c.trim()).filter(Boolean));
}

/**
 * Renders an embedded image's analysis as a figure instead of a wall of JSON.
 *
 * The pipeline replaces images with their vision analysis so the text carries
 * the content, but that payload was reaching the reader verbatim — braces,
 * escaped newlines and all — in the middle of an otherwise readable document.
 *
 * When the image was a table, `extracted_text` is laid out as a grid; otherwise
 * it is shown as preformatted text. Either way the original wording is
 * preserved exactly, because a reviewer is approving regulatory text and must
 * see what the document actually says.
 */
export const VisionFigure: FC<{ data: VisionAnalysis }> = observer(({ data }) => {
  const theme = useTheme();
  const { t } = useTrans();

  const isTable = (data.image_type ?? '').toLowerCase() === 'table';
  const rows = isTable && data.extracted_text ? toRows(data.extracted_text) : [];
  const Icon = isTable ? TableIcon : ImageSquareIcon;

  return (
    <Box
      component="figure"
      sx={{
        m: 0,
        my: 2,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.75,
          py: 1.25,
          backgroundColor: theme.palette.grey[200],
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <Icon size={15} color={theme.palette.text.secondary} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, flex: 1, minWidth: 0 }} noWrap>
          {data.title || t('app.remediation.figure.untitled')}
        </Typography>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'text.tertiary',
          }}
        >
          {t('app.remediation.figure.fromImage')}
        </Typography>
      </Box>

      <Box sx={{ px: 1.75, py: 1.5 }}>
        {data.summary && (
          <Typography
            sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}
          >
            {data.summary}
          </Typography>
        )}

        {rows.length > 0 ? (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              component="table"
              sx={{
                borderCollapse: 'collapse',
                width: 'max-content',
                minWidth: '100%',
                fontSize: 12,
                '& td': {
                  border: `1px solid ${theme.palette.grey[300]}`,
                  px: 1,
                  py: 0.6,
                  verticalAlign: 'top',
                },
              }}
            >
              <Box component="tbody">
                {rows.map((cells, i) => (
                  <Box component="tr" key={i}>
                    {cells.map((cell, j) => (
                      <Box component="td" key={j}>
                        {cell}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          data.extracted_text && (
            <Box
              component="pre"
              sx={{
                m: 0,
                fontSize: 12,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                fontFamily: 'inherit',
                color: 'text.primary',
              }}
            >
              {data.extracted_text}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
});
